import React, { useCallback, useEffect, useState, useReducer, useMemo } from 'react';

import { useTranslate } from '@src/core/hooks';
import { Scheduling, RescheduleScheduling, FeaturedTariffPreviewMode } from '@src/core/buyerApiClient';
import CalendarLegend from '@src/components/reusable/Calendar/CalendarLegend';
import {
  initialMonth,
  monthNames,
  MonthTuple,
  IntervalDate,
  mountInterval,
  changeMonth,
  dateToObj,
  mergeIntervalWithDates,
} from '@src/common/calendar';
import CalendarHeader from './CalendarHeader';
import CalendarMonthView from './CalendarMonthView';
import CalendarYearView from './CalendarYearView';
import { Container } from './styles';

type DateProps = {
  [key: string]: {
    availables: Scheduling[] | RescheduleScheduling[];
    unavailables: Scheduling[] | RescheduleScheduling[];
  };
};

type Props = {
  dates: DateProps;
  pending: boolean;
  defaultDateReference?: string;
  onMonthChange?: (monthTuple: MonthTuple, interval: IntervalDate[]) => void;
  onDateSelect: (date: string) => void;
  enableAutoLoad?: boolean;
  tariffToShowThePrice?: FeaturedTariffPreviewMode | null;
};

const Calendar = (props: Props) => {
  const { dates, pending, defaultDateReference, onMonthChange, onDateSelect, enableAutoLoad, tariffToShowThePrice } =
    props;

  const { i18n } = useTranslate();
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const [monthTuple, setMonthTuple] = useState<MonthTuple>(() => {
    if (defaultDateReference) {
      const [year, month] = dateToObj(new Date(defaultDateReference));
      return [year, month];
    }

    return initialMonth();
  });
  const interval = useMemo(() => mountInterval(monthTuple), [monthTuple]);
  const [autoLoadCount, decreaseAutoLoadCount] = useReducer((num) => num - 1, 5);

  // Handle month auto load
  useEffect(() => {
    if (enableAutoLoad && autoLoadCount > 0 && !pending && Object.keys(dates).length === 0) {
      decreaseAutoLoadCount();
      setMonthTuple(changeMonth(monthTuple, 1));
    }
  }, [enableAutoLoad, dates, pending]);

  useEffect(() => {
    if (onMonthChange) {
      onMonthChange(monthTuple, interval);
    }
  }, [monthTuple]);

  const filteredDates = mergeIntervalWithDates(interval, dates);
  const [year, month] = monthTuple;

  const calculateNextOrPreviousMonth = (step: number) => {
    switch (mode) {
      case 'month':
        const newMonth = changeMonth(monthTuple, step);
        setMonthTuple(newMonth);
        break;
      case 'year':
        const newTuple: MonthTuple = [year + step, month];
        setMonthTuple(newTuple);
        break;
    }
  };

  const handlePrevious = () => calculateNextOrPreviousMonth(-1);
  const handleNext = () => calculateNextOrPreviousMonth(1);

  const handleMonthVisualization = (nextTuple: MonthTuple) => {
    setMode('month');
    setMonthTuple(nextTuple);
  };

  const handleDateSelect = useCallback((date: string) => {
    onDateSelect(date);
  }, []);

  return (
    <Container>
      <CalendarLegend />

      <CalendarHeader onNext={handleNext} onPrevious={handlePrevious} onModeChange={() => setMode('year')}>
        {mode === 'month' ? `${i18n(monthNames[month - 1])} ${year}` : year}
      </CalendarHeader>

      {mode == 'month' ? (
        <CalendarMonthView
          pending={pending}
          monthTuple={monthTuple}
          filteredDates={filteredDates}
          onSelectDate={handleDateSelect}
          tariffToShowThePrice={tariffToShowThePrice}
        />
      ) : null}

      {mode == 'year' ? <CalendarYearView year={year} onMonthClick={handleMonthVisualization} /> : null}
    </Container>
  );
};

export type { DateProps as CalendarDateProps };
export default Calendar;
